import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import axios from "axios";
import { Entry, Height } from "../../../types/entry";
import { ShowEntry } from "../component";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  Skeleton,
  Grid,
  Divider,
  IconButton,
  Link,
  useTheme,
  Fade,
  Container,
  Stack,
  Paper,
} from "@mui/material";
import {
  ArrowBack,
  Schedule,
  Map,
  TrendingUp,
  Person,
  School,
  Save,
  Warning,
  CheckCircle,
} from "@mui/icons-material";

const EntryOverview = (): JSX.Element => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [show, setShow] = useState<ShowEntry>({} as ShowEntry);
  // const [paid, setPaid] = useState<boolean>(false);
  const [entryOverview, setEntryOverview] = useState<any>({});
  const [possibleHeights, setPossibleHeights] = useState<string[]>([]);
  const [changed, setChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleChange = (index: number, e: any) => {
    setChanged(true);
    const updatedEntries = [...entries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      [e.target.name]: e.target.value,
    };
    setEntries(updatedEntries);
  };

  const getEntryOverview = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const token = new Cookies().get("token");

      const response = await axios.get(
        `https://api.easyagility.co.uk/entry-overview?show_id=${id}`,
        { headers: { Authorization: "Bearer " + token } },
      );

      setEntries(response.data.entries);
      // setPaid(response.data.paid);
      setEntryOverview(response.data);
    } catch (err) {
      setError("Failed to load entry overview. Please try again later.");
      console.error("Error fetching entry overview:", err);
    } finally {
      setLoading(false);
    }
  };

  const getShow = async () => {
    try {
      const token = new Cookies().get("token");

      const response = await axios.get(
        "https://api.easyagility.co.uk/show-entries",
        {
          headers: { Authorization: "Bearer " + token },
        },
      );

      if (id) {
        const show = response.data?.find(
          (show: ShowEntry) => show.id === parseInt(id),
        );
        if (show) {
          setShow(show);
          if (show.show_name.includes("Babel")) {
            setPossibleHeights([
              "200",
              Height.Small,
              Height.Medium,
              Height.Intermediate,
              Height.Large,
            ]);
          } else if (show.show_name.includes("Performance")) {
            setPossibleHeights([
              Height.Micro,
              Height.Small,
              Height.Medium,
              Height.Intermediate,
              Height.Large,
            ]);
          } else {
            setPossibleHeights([
              Height.Anysize,
              Height.Small,
              Height.Medium,
              Height.Intermediate,
              Height.Large,
            ]);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching show:", err);
    }
  };

  const toggleWithdraw = async (id: number) => {
    try {
      const token = new Cookies().get("token");

      await axios.post(
        `https://api.easyagility.co.uk/entry-withdraw?entry_id=${id}`,
        {},
        { headers: { Authorization: "Bearer " + token } },
      );
      getShow();
    } catch (err) {
      console.error("Error toggling withdraw:", err);
    }
  };

  const updateEntry = async () => {
    if (id) {
      try {
        setSaving(true);
        let eo = { ...entryOverview };
        eo.entries = entries;

        const response = await axios.post(
          "https://api.easyagility.co.uk/entry-update",
          eo,
        );

        if (response.status === 200) {
          navigate("/");
        }
      } catch (err) {
        console.error("Error updating entry:", err);
        setError("Failed to save changes. Please try again.");
      } finally {
        setSaving(false);
      }
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return date.toLocaleDateString("en-GB", options);
  };

  const isShowEditable = () => {
    return (
      new Date(show.show_date) > new Date() && show.id !== 45 && show.id !== 46
    );
  };

  const getHeightLabel = (height: string) => {
    const heightMap: { [key: string]: string } = {
      "200": "Any Size",
      "300": "Small",
      "400": "Medium",
      "500": "Intermediate",
      "600": "Large",
      "200/300": "Any Size",
      "250": "Micro",
    };
    return heightMap[height] || height;
  };

  /** Unpaid entries from entry_overview (entries array when show_entries is empty) */
  const unpaidEntries = (): Array<{
    dog_name: string;
    handler_first_name: string;
    handler_last_name: string;
    height: string;
    grade: number;
    class_names: string[];
  }> => {
    const list =
      (entryOverview as { entries?: unknown[] })?.entries ?? entries ?? [];
    if (!Array.isArray(list)) return [];
    return list.map((e: unknown) => {
      const row = e as Record<string, unknown>;
      return {
        dog_name: String(row.dog_name ?? ""),
        handler_first_name: String(row.handler_first_name ?? ""),
        handler_last_name: String(row.handler_last_name ?? ""),
        height: String(row.height ?? ""),
        grade: Number(row.grade ?? 0),
        class_names: Array.isArray(row.class_details)
          ? ((row.class_details as { class_name?: string }[])
              .map((c) => c.class_name)
              .filter(Boolean) as string[])
          : [],
      };
    });
  };

  useEffect(() => {
    if (id) {
      getEntryOverview(parseInt(id));
      getShow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="60%" height={48} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Grid container spacing={3}>
          {[...Array(3)].map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ height: 300 }}>
                <CardContent>
                  <Skeleton variant="text" width="80%" height={32} />
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={120}
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              mr: 2,
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.light + "20",
              },
            }}
            aria-label="Go back to entries"
          >
            <ArrowBack />
          </IconButton>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: { xs: "1.8rem", md: "2.5rem" },
            }}
          >
            {show.show_name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            mb: 3,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "1rem", md: "1.1rem" },
            }}
          >
            {formatDate(show.show_date)}
          </Typography>
          <Chip
            label={(show?.show_entries?.length ?? 0) === 0 ? "Unpaid" : "Paid"}
            color={
              (show?.show_entries?.length ?? 0) === 0 ? "warning" : "success"
            }
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>

      {/* Quick Links */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: theme.palette.text.primary,
          }}
        >
          Show Information
        </Typography>
        <Grid container spacing={2}>
          {show.schedule_url && (
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Schedule />}
                onClick={() => window.open(show.schedule_url, "_blank")}
                sx={{
                  py: 1.5,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.light + "10",
                  },
                }}
              >
                View Schedule
              </Button>
            </Grid>
          )}
          {show.ring_plan_url && (
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Map />}
                onClick={() => window.open(show.ring_plan_url, "_blank")}
                sx={{
                  py: 1.5,
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.main,
                  "&:hover": {
                    borderColor: theme.palette.secondary.dark,
                    backgroundColor: theme.palette.secondary.light + "10",
                  },
                }}
              >
                View Ring Plan
              </Button>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<TrendingUp />}
              onClick={() =>
                window.open(
                  "https://results.easyagility.co.uk/previous-shows",
                  "_blank",
                )
              }
              sx={{
                py: 1.5,
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
                "&:hover": {
                  borderColor: theme.palette.info.dark,
                  backgroundColor: theme.palette.info.light + "10",
                },
              }}
            >
              View Results
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Cancellation notice */}
      <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 600, display: "block", mb: 1 }}
        >
          Cancelling your entry
        </Typography>
        <Typography variant="body2" component="span">
          If you wish to cancel your entry you must contact Matt at{" "}
          <Link
            href="mailto:matt@easyagility.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600 }}
          >
            matt@easyagility.co.uk
          </Link>
          . Please note that refunds will normally only be issued if the request
          is received before the show closing date and the show has not yet
          reached its cap. If in doubt, read the show schedule or contact the
          show manager before making a request to cancel.
        </Typography>
      </Alert>

      {/* Error State */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 4, borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => getEntryOverview(parseInt(id!))}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Entries: use show_entries (from /show-entries) to determine paid vs unpaid */}
      {(show?.show_entries?.length ?? 0) === 0 ? (
        <Stack spacing={3}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 3,
              borderColor: theme.palette.warning.main,
              backgroundColor: theme.palette.warning.light + "15",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Warning
                sx={{ fontSize: 28, color: theme.palette.warning.main }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                Unpaid entries summary
              </Typography>
            </Box>
            {(() => {
              const o = entryOverview as Record<string, unknown>;
              const ref = o?.reference as string | undefined;
              const totalOwing = o?.total_owing as string | undefined;
              const showFees = o?.show_fees as string | undefined;
              const list = unpaidEntries();
              if (list.length === 0) {
                return (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    No unpaid entries for this show.
                  </Typography>
                );
              }
              return (
                <>
                  {totalOwing != null && totalOwing !== "" && ref && (
                    <>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: theme.palette.text.primary,
                        }}
                      >
                        How to pay (total owing: £{totalOwing})
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: theme.palette.text.secondary }}
                      >
                        You can pay by bank transfer using the details below.
                        Please use your reference as the payment reference so we
                        can match your payment.
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          mb: 3,
                          bgcolor: theme.palette.background.default,
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Account name:</strong> Easy Agility
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Sort code:</strong> 04-00-03
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Account number:</strong> 66835282
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            color: theme.palette.warning.dark,
                            fontWeight: 600,
                          }}
                        >
                          <strong>Payment reference:</strong> {ref}
                        </Typography>
                      </Paper>
                    </>
                  )}
                  {(ref || totalOwing != null || showFees != null) && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 3,
                        pb: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      {ref && (
                        <Typography variant="body2">
                          <strong>Reference:</strong> {ref}
                        </Typography>
                      )}
                      {showFees != null && showFees !== "" && (
                        <Typography variant="body2">
                          <strong>Show fees:</strong> £{showFees}
                        </Typography>
                      )}
                      {totalOwing != null && totalOwing !== "" && (
                        <Typography variant="body2">
                          <strong>Total owing:</strong> £{totalOwing}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Stack spacing={2}>
                    {list.map((entry, idx) => (
                      <Card
                        key={idx}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <CardContent sx={{ py: 2, px: 3 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            {entry.dog_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {entry.handler_first_name} {entry.handler_last_name}
                            {" · "}
                            {getHeightLabel(entry.height)}
                            {" · "}
                            Grade {entry.grade}
                          </Typography>
                          {entry.class_names.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                mt: 1,
                              }}
                            >
                              {entry.class_names.map((name, i) => (
                                <Chip
                                  key={i}
                                  label={name}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 500 }}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </>
              );
            })()}
          </Paper>
        </Stack>
      ) : (
        <Grid container spacing={3}>
          {(entries ?? []).map((entry, index) => (
            <Grid item xs={12} lg={6} key={entry.dog_name}>
              <Fade in timeout={300}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    boxShadow: theme.shadows[2],
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Entry Header */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          mb: 1,
                        }}
                      >
                        {entry.dog_name}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Person
                          sx={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                            mr: 1,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                          }}
                        >
                          {entry.handler_first_name} {entry.handler_last_name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <School
                          sx={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                            mr: 1,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Classes:{" "}
                          {Array.from(
                            new Set(
                              show.show_entries?.map(
                                (entry) => entry.class_name,
                              ),
                            ),
                          ).join(", ")}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Editable Fields */}
                    {isShowEditable() ? (
                      <Box>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Height</InputLabel>
                              <Select
                                value={entry.height}
                                label="Height"
                                name="height"
                                onChange={(e) => handleChange(index, e)}
                                sx={{ borderRadius: 2 }}
                              >
                                {possibleHeights.map((height) => (
                                  <MenuItem key={height} value={height}>
                                    {getHeightLabel(height)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Grade</InputLabel>
                              <Select
                                value={entry.grade}
                                label="Grade"
                                name="grade"
                                onChange={(e) => handleChange(index, e)}
                                sx={{ borderRadius: 2 }}
                              >
                                {[1, 2, 3, 4, 5, 6, 7].map((grade) => (
                                  <MenuItem key={grade} value={grade}>
                                    Grade {grade}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>

                        {/* Class Withdrawals */}
                        {!changed && (
                          <Box sx={{ mb: 3 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: theme.palette.text.primary,
                              }}
                            >
                              Class Withdrawals
                            </Typography>
                            <Stack spacing={1}>
                              {show.show_entries
                                ?.filter(
                                  (se) =>
                                    se.dog_name === entry.dog_name &&
                                    (se.time === 0 || !se.time) &&
                                    !se.eliminated,
                                )
                                .map((se) => (
                                  <Box
                                    key={se.id}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      p: 2,
                                      borderRadius: 2,
                                      backgroundColor:
                                        theme.palette.background.default,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {se.class_name}
                                    </Typography>
                                    <Button
                                      size="small"
                                      color={se.withdrawn ? "success" : "error"}
                                      variant="outlined"
                                      onClick={() => toggleWithdraw(se.id)}
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: "none",
                                        minWidth: 120,
                                      }}
                                    >
                                      {se.withdrawn ? "Re-enter" : "Withdraw"}
                                    </Button>
                                  </Box>
                                ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Button
                            variant="contained"
                            startIcon={<Save />}
                            disabled={!changed || saving}
                            onClick={updateEntry}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              px: 3,
                            }}
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      /* Read-only View */
                      <Box>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.secondary,
                                  minWidth: 80,
                                }}
                              >
                                Height:
                              </Typography>
                              <Chip
                                label={getHeightLabel(entry.height)}
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.secondary,
                                  minWidth: 80,
                                }}
                              >
                                Grade:
                              </Typography>
                              <Chip
                                label={`Grade ${entry.grade}`}
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Class Results */}
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              mb: 2,
                              color: theme.palette.text.primary,
                            }}
                          >
                            Class Results
                          </Typography>
                          <Stack spacing={1}>
                            {show.show_entries
                              ?.filter(
                                (se) =>
                                  se.dog_name === entry.dog_name &&
                                  (se.time !== 0 || se.eliminated),
                              )
                              .map((se) => (
                                <Box
                                  key={se.id}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.default,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {se.class_name}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {se.eliminated ? (
                                      <Chip
                                        label="Eliminated"
                                        color="error"
                                        size="small"
                                        icon={<Warning />}
                                      />
                                    ) : se.faults.length > 0 ? (
                                      <Chip
                                        label={`${se.time}s (${se.faults.join(
                                          ", ",
                                        )})`}
                                        color="warning"
                                        size="small"
                                      />
                                    ) : (
                                      <Chip
                                        label={`${se.time}s - CLEAR`}
                                        color="success"
                                        size="small"
                                        icon={<CheckCircle />}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              ))}
                          </Stack>
                        </Box>

                        {/* Withdrawal Options */}
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              mb: 2,
                              color: theme.palette.text.primary,
                            }}
                          >
                            Class Withdrawals
                          </Typography>
                          <Stack spacing={1}>
                            {show.show_entries
                              ?.filter(
                                (se) =>
                                  se.dog_name === entry.dog_name &&
                                  (se.time === 0 || !se.time) &&
                                  !se.eliminated,
                              )
                              .map((se) => (
                                <Box
                                  key={se.id}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.default,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {se.class_name}
                                  </Typography>
                                  <Button
                                    size="small"
                                    color={se.withdrawn ? "success" : "error"}
                                    variant="outlined"
                                    onClick={() => toggleWithdraw(se.id)}
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: "none",
                                      minWidth: 120,
                                    }}
                                  >
                                    {se.withdrawn ? "Re-enter" : "Withdraw"}
                                  </Button>
                                </Box>
                              ))}
                          </Stack>
                        </Box>

                        <Button
                          variant="outlined"
                          startIcon={<ArrowBack />}
                          onClick={() => navigate("/")}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            px: 3,
                          }}
                        >
                          Back to Entries
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default EntryOverview;
